import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldStar, Heart, ChatCircleDots, MagnifyingGlass, Trash, Users, Envelope, ShareNetwork } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AdminTestimonials from "@/components/AdminTestimonials";
import AdminIdCampaign from "@/components/AdminIdCampaign";

function StatCard({ icon: Icon, label, value, color = "orange" }) {
  return (
    <div className="bg-white rounded-3xl border border-rose-100 p-6 flex items-center gap-4">
      <div className={`w-14 h-14 rounded-2xl bg-${color}-100 text-${color}-600 grid place-items-center`}>
        <Icon size={26} weight="duotone" />
      </div>
      <div>
        <p className="text-3xl font-display font-black text-stone-800" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
        <p className="text-sm text-stone-500">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/connexion"); return; }
    if (!user.is_admin) { toast.error("Accès réservé aux administrateurs"); navigate("/dashboard"); return; }
    (async () => {
      const [s, r, u, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/found-reports?limit=10"),
        api.get("/admin/users?limit=10"),
        api.get("/admin/contact-messages?limit=50"),
      ]);
      setStats(s.data); setRecentReports(r.data); setRecentUsers(u.data); setContactMessages(m.data);
    })().catch(() => toast.error("Erreur de chargement"));
  }, [user, loading, navigate]);

  const deleteReport = async (id) => {
    if (!window.confirm("Supprimer ce signalement ?")) return;
    await api.delete(`/admin/found-reports/${id}`);
    setRecentReports(rs => rs.filter(r => r.report_id !== id));
    toast.success("Signalement supprimé");
  };

  const exportSocialImage = async (id) => {
    const t = toast.loading("Génération de l'image…");
    try {
      const res = await api.get(`/admin/found-reports/${id}/social-image`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `doudou-trouve-${id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Image téléchargée — prête à publier !", { id: t });
    } catch (e) {
      toast.error("Impossible de générer l'image", { id: t });
    }
  };

  const markContactTreated = async (id) => {
    await api.patch(`/admin/contact-messages/${id}`, null, { params: { status: "treated" } });
    setContactMessages(ms => ms.map(m => m.message_id === id ? { ...m, status: "treated" } : m));
    toast.success("Marqué comme traité");
  };

  if (!stats) return <Layout><div className="p-12">Chargement...</div></Layout>;

  const newMessagesCount = contactMessages.filter(m => m.status === "new").length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <ShieldStar size={36} weight="duotone" className="text-orange-500" />
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Espace administrateur</span>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-800" data-testid="admin-title">Tableau de bord admin</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-rose-100 overflow-x-auto">
          {[
            { k: "overview", label: "Vue d'ensemble" },
            { k: "messages", label: `Messages reçus${newMessagesCount > 0 ? ` (${newMessagesCount})` : ""}` },
            { k: "users", label: "Utilisateurs" },
            { k: "reports", label: "Signalements" },
            { k: "testimonials", label: "Témoignages" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-3 font-bold text-sm whitespace-nowrap transition border-b-2 -mb-px ${tab === t.k ? "border-orange-500 text-orange-600" : "border-transparent text-stone-500 hover:text-orange-600"}`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Familles" value={stats.users} color="orange" />
              <StatCard icon={Heart} label="Doudous" value={stats.doudous} color="rose" />
              <StatCard icon={MagnifyingGlass} label="Signalements" value={stats.found_reports} color="orange" />
              <StatCard icon={ChatCircleDots} label="Conversations" value={stats.conversations} color="rose" />
            </div>
            {newMessagesCount > 0 && (
              <button onClick={() => setTab("messages")} className="mt-6 w-full bg-rose-50 border border-rose-200 rounded-2xl p-4 text-left hover:bg-rose-100 transition" data-testid="admin-new-messages-alert">
                <p className="font-display font-bold text-rose-700">📬 {newMessagesCount} nouveau{newMessagesCount > 1 ? "x" : ""} message{newMessagesCount > 1 ? "s" : ""} à traiter</p>
                <p className="text-sm text-stone-600">Cliquez pour voir les messages de contact en attente.</p>
              </button>
            )}
            <AdminIdCampaign />
            <div className="mt-8 bg-amber-50 border-2 border-amber-200 rounded-3xl p-5" data-testid="admin-wipe-section">
              <p className="font-display font-bold text-amber-800">🧹 Nettoyer les données de test</p>
              <p className="text-sm text-stone-600 mt-1">
                Supprime tous les comptes, doudous, signalements, conversations, messages, abonnements et messages
                de contact <strong>sauf votre propre compte admin</strong>. Action irréversible.
              </p>
              <button
                onClick={async () => {
                  if (!window.confirm("Confirmer la suppression de TOUTES les données de test ?")) return;
                  try {
                    const { data } = await api.post("/admin/wipe-test-data");
                    alert("Données effacées : " + JSON.stringify(data.deleted, null, 2));
                    window.location.reload();
                  } catch (e) {
                    alert("Erreur : " + (e.response?.data?.detail || e.message));
                  }
                }}
                className="mt-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-full px-5 py-2.5"
                data-testid="admin-wipe-button"
              >
                Effacer toutes les données de test
              </button>
            </div>
          </>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages-list">
            {contactMessages.length === 0 ? (
              <div className="bg-white rounded-3xl border border-rose-100 p-10 text-center">
                <Envelope size={56} weight="duotone" className="mx-auto text-rose-300" />
                <p className="mt-4 text-stone-500">Aucun message pour le moment.</p>
              </div>
            ) : contactMessages.map((m) => (
              <div key={m.message_id} className={`bg-white rounded-2xl border p-5 ${m.status === "new" ? "border-rose-300" : "border-rose-100 opacity-75"}`} data-testid={`admin-message-${m.message_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-stone-800">{m.name}</p>
                      <a href={`mailto:${m.email}`} className="text-sm text-orange-600 hover:underline">{m.email}</a>
                      {m.status === "new" ? (
                        <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">NOUVEAU</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">Traité</span>
                      )}
                    </div>
                    <p className="text-sm text-orange-600 font-bold mt-1">{m.subject}</p>
                    <p className="text-stone-700 mt-2 whitespace-pre-wrap text-sm">{m.message}</p>
                    <p className="text-xs text-stone-400 mt-2">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} data-testid={`admin-reply-${m.message_id}`}>
                      <Button size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full font-bold whitespace-nowrap">Répondre</Button>
                    </a>
                    {m.status === "new" && (
                      <Button size="sm" variant="outline" onClick={() => markContactTreated(m.message_id)} className="rounded-full font-bold" data-testid={`admin-mark-treated-${m.message_id}`}>
                        Marquer traité
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-2">
            {recentUsers.map(u => (
              <div key={u.user_id} className="bg-white rounded-2xl border border-rose-100 p-4" data-testid={`admin-user-${u.user_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-stone-800">
                      {u.name} {u.last_name ? (
                        <span className="text-stone-700 font-bold" data-testid={`admin-user-lastname-${u.user_id}`}>{u.last_name}</span>
                      ) : (
                        <span className="text-stone-400 font-normal italic text-sm" data-testid={`admin-user-nolastname-${u.user_id}`}>(nom non renseigné)</span>
                      )}
                      {u.is_admin && <span className="ml-2 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">ADMIN</span>}
                    </p>
                    <p className="text-sm text-stone-500">
                      <a href={`mailto:${u.email}`} className="hover:text-orange-600" data-testid={`admin-user-email-${u.user_id}`}>{u.email}</a>
                      <span> · {u.auth_provider}</span>
                    </p>
                  </div>
                  <p className="text-xs text-stone-400">{new Date(u.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-2">
            {recentReports.length === 0 ? <p className="text-stone-500">Aucun signalement.</p> : recentReports.map(r => (
              <div key={r.report_id} className="bg-white rounded-2xl border border-rose-100 p-4 flex items-center justify-between gap-3" data-testid={`admin-report-${r.report_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-stone-800">
                    {r.finder_first_name}{r.finder_last_name ? <span className="font-bold"> {r.finder_last_name}</span> : null}
                  </p>
                  {r.finder_email && (
                    <p className="text-xs text-stone-500">
                      <a href={`mailto:${r.finder_email}`} className="hover:text-orange-600" data-testid={`admin-report-email-${r.report_id}`}>{r.finder_email}</a>
                    </p>
                  )}
                  <p className="text-sm text-stone-500 line-clamp-1 mt-1">{r.description}</p>
                  <p className="text-xs text-stone-400 mt-1">{new Date(r.created_at).toLocaleString("fr-FR")}</p>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2 shrink-0">
                  <Button
                    onClick={() => exportSocialImage(r.report_id)}
                    variant="outline"
                    className="rounded-full border-orange-200 text-orange-600 hover:bg-orange-50 gap-2 h-9 px-3"
                    title="Télécharger le visuel prêt à poster sur Instagram / Facebook"
                    data-testid={`admin-export-social-${r.report_id}`}
                  >
                    <ShareNetwork size={16} weight="bold" />
                    <span className="text-xs font-semibold">Réseaux sociaux</span>
                  </Button>
                  <Button onClick={() => deleteReport(r.report_id)} variant="ghost" className="text-rose-500 hover:bg-rose-50 rounded-full" data-testid={`admin-delete-report-${r.report_id}`}>
                    <Trash size={18} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === "testimonials" && <AdminTestimonials />}
      </div>
    </Layout>
  );
}
